import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Lock, ArrowLeft, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_caregiver-hub-25/artifacts/7i9n9nvr_Zorg%20Vleugel%20Logo%20Icoon%20Rood.png";

// Password requirements
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'Minimaal 8 tekens', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'Minimaal één hoofdletter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'Minimaal één kleine letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'Minimaal één cijfer', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'Minimaal één speciaal teken (!@#$%^&*)', test: (p) => /[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~]/.test(p) },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setValidating(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const passwordStrength = PASSWORD_REQUIREMENTS.filter(req => req.test(password)).length;
  const allRequirementsMet = passwordStrength === PASSWORD_REQUIREMENTS.length;
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!allRequirementsMet) {
      toast.error('Wachtwoord voldoet niet aan alle eisen');
      return;
    }

    if (!passwordsMatch) {
      toast.error('Wachtwoorden komen niet overeen');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password,
      });
      setSuccess(true);
      toast.success('Wachtwoord succesvol gewijzigd!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Er is iets misgegaan');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f] mx-auto mb-4" />
          <p className="text-slate-600">Link valideren...</p>
        </div>
      </div>
    );
  }

  // Invalid or missing token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              Ongeldige Link
            </h1>
            <p className="text-slate-600 mb-6">
              Deze reset link is ongeldig of verlopen. Vraag een nieuwe link aan.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87]"
              >
                Nieuwe Link Aanvragen
              </Button>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#2d5a87] font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Terug naar inloggen
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-md"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              Wachtwoord Gewijzigd!
            </h1>
            <p className="text-slate-600 mb-6">
              Je wachtwoord is succesvol gewijzigd. Je kunt nu inloggen met je nieuwe wachtwoord.
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full h-12 rounded-xl bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87]"
              data-testid="go-to-login-btn"
            >
              Naar Inloggen
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src={LOGO_URL} alt="Zorg-Vleugel" className="h-16 w-16" />
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              Nieuw Wachtwoord
            </h1>
            <p className="text-slate-600">
              Kies een nieuw wachtwoord voor <span className="font-medium">{userEmail}</span>
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">
                Nieuw Wachtwoord
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                  className="h-12 pl-12 pr-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-50 rounded-xl p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Wachtwoord eisen (NEN 7510):</p>
              {PASSWORD_REQUIREMENTS.map((req) => {
                const met = req.test(password);
                return (
                  <div key={req.id} className="flex items-center gap-2 text-sm">
                    {met ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={met ? 'text-green-700' : 'text-slate-500'}>
                      {req.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-700 font-medium">
                Bevestig Wachtwoord
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className={`h-12 pl-12 pr-12 bg-white border-slate-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]/20 rounded-xl ${
                    confirmPassword && !passwordsMatch ? 'border-red-300 focus:border-red-500' : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && !passwordsMatch && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  Wachtwoorden komen niet overeen
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full h-12 rounded-xl text-base font-semibold bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87] hover:from-[#2d5a87] hover:to-[#3d6a97] shadow-lg shadow-[#1e3a5f]/25 transition-all duration-300"
              disabled={loading || !allRequirementsMet || !passwordsMatch}
              data-testid="reset-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Wachtwoord Wijzigen'
              )}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#2d5a87] font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Terug naar inloggen
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-slate-500 mt-6">
          © 2026 Zorg-Vleugel • Begeleid Wonen
        </p>
      </motion.div>
    </div>
  );
}
